import { useRef } from 'react';
import { useWatch, useFormContext, FieldValues, Path } from 'react-hook-form';

type ValuesFor<T extends FieldValues, N extends readonly Path<T>[]> = { [K in N[number]]: T[K] };

const shallowEqualArray = (a: readonly unknown[], b: readonly unknown[]) =>
  a.length === b.length && a.every((value, i) => value === b[i]);

export const useWatchFields = <
  TFieldValues extends FieldValues,
  TNames extends readonly Path<TFieldValues>[] = Path<TFieldValues>[],
>(
  names: TNames,
): ValuesFor<TFieldValues, TNames> => {
  const { control } = useFormContext<TFieldValues>();
  const namesRef = useRef(names);
  const stableNames = namesRef.current;
  const values = useWatch({ control, name: stableNames }) as Path<TFieldValues>[];
  const resultRef = useRef<{ values: Path<TFieldValues>[]; result: ValuesFor<TFieldValues, TNames> } | undefined>(
    undefined,
  );

  if (!resultRef.current || !shallowEqualArray(resultRef.current.values, values)) {
    resultRef.current = {
      values,
      result: Object.fromEntries(stableNames.map((n, i) => [n, values[i]])) as ValuesFor<TFieldValues, TNames>,
    };
  }

  return resultRef.current.result;
};
